import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Plus } from "lucide-react";

export type SavedLookRow = {
  id: string;
  image_url: string;
  image_path?: string | null;
  image_paths?: string[] | null;
  lookbook_image_url?: string | null;
  created_at: string;
  occasion?: string | null;
  title?: string | null;
  analysis_mode?: string | null;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export function SavedLooksGrid({
  userId,
  analysisMode,
  onViewOutfit,
  onGetNewLook,
  refreshKey = 0,
}: {
  userId: string;
  analysisMode: "find_look" | "style_item";
  onViewOutfit: (outfit: SavedLookRow) => void;
  onGetNewLook: () => void;
  refreshKey?: number;
}) {
  const [items, setItems] = useState<SavedLookRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("outfits")
        .select(
          "id, image_url, image_path, image_paths, lookbook_image_url, created_at, occasion, title, analysis_mode",
        )
        .eq("user_id", userId)
        .eq("is_saved", true)
        .eq("analysis_mode", analysisMode)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      const rows = ((data ?? []) as unknown) as SavedLookRow[];
      setItems(rows);
      const entries = await Promise.all(
        rows.map(async (r) => {
          if (r.lookbook_image_url) return [r.id, r.lookbook_image_url] as const;
          const path = r.image_path ?? r.image_paths?.[0] ?? null;
          if (!path) return [r.id, r.image_url ?? ""] as const;
          const { data: s } = await supabase.storage
            .from("outfits")
            .createSignedUrl(path, 60 * 60);
          return [r.id, s?.signedUrl ?? r.image_url ?? ""] as const;
        }),
      );
      if (cancelled) return;
      setSigned(Object.fromEntries(entries.filter(([, u]) => u)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, analysisMode, refreshKey]);

  if (loading) return null;

  if (items.length === 0) {
    return (
      <div
        className="l2f-empty-state"
        style={{
          background: "var(--l2f-cream)",
          borderRadius: 20,
          padding: "44px 24px",
          margin: "8px 0",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#FF5C5C",
          }}
        >
          The Archive
        </span>
        <p
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: 28,
            lineHeight: 1.05,
            color: "var(--l2f-ink)",
            margin: "4px 0 2px",
          }}
        >
          Nothing saved yet.
        </p>
        <p
          className="l2f-empty-state-subtitle"
          style={{ fontFamily: "var(--font-body)" }}
        >
          Tap the heart on any result to start your edit.
        </p>
        <button
          onClick={onGetNewLook}
          className="l2f-btn-primary"
          style={{ width: "auto", marginTop: 10, padding: "12px 22px" }}
        >
          {analysisMode === "find_look" ? "Get a look →" : "Style an item →"}
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 px-1">
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onViewOutfit(it)}
          className="text-left overflow-hidden group"
          style={{
            borderRadius: 14,
            border: "0.5px solid hsl(var(--border))",
            cursor: "pointer",
            background: "hsl(var(--card))",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
        >
          <div style={{ aspectRatio: "1 / 1.4", width: "100%", background: "var(--l2f-cream)", position: "relative" }}>
            {signed[it.id] && (
              <img
                src={signed[it.id]}
                alt={it.title ?? "Saved outfit"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            {it.occasion && (
              <span
                style={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  background: "rgba(255,255,255,0.92)",
                  color: "var(--l2f-ink)",
                  borderRadius: 999,
                  padding: "3px 9px",
                  fontFamily: "var(--font-body)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  backdropFilter: "blur(6px)",
                }}
              >
                {it.occasion}
              </span>
            )}
          </div>
          <div style={{ background: "hsl(var(--card))", padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 15,
                color: "var(--l2f-ink)",
                lineHeight: 1,
              }}
            >
              Look
            </span>
            <span
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              {formatDate(it.created_at)}
            </span>
          </div>
        </button>
      ))}
      <button
        onClick={onGetNewLook}
        className="flex flex-col items-center justify-center gap-2"
        style={{
          borderRadius: 14,
          border: "1.5px dashed #FF5C5C",
          cursor: "pointer",
          background: "var(--l2f-cream)",
          aspectRatio: "1 / 1.4",
          color: "#FF5C5C",
        }}
      >
        <Plus size={22} style={{ color: "#FF5C5C" }} />
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 16,
            color: "var(--l2f-ink)",
          }}
        >
          New look
        </span>
      </button>
    </div>
  );
}
